import { installMotion, reducedMotion } from '@snitch/ui/lib/motion'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { createRouter, RouterProvider } from '@tanstack/react-router'
import { StrictMode } from 'react'
import { createRoot } from 'react-dom/client'
import { getMiniAppInvitation, signInWithMiniApp } from './lib/auth.ts'
import { inTelegram, setupTelegram } from './lib/telegram.ts'
import { applyTheme, readThemePreference } from './lib/theme.ts'
import { routeTree } from './routeTree.gen.ts'
import './styles.css'

const queryClient = new QueryClient({
  defaultOptions: {
    queries: { staleTime: 30_000, retry: 1 },
  },
})

const router = createRouter({
  routeTree,
  context: { queryClient },
  defaultPreload: 'intent',
  scrollRestoration: true,
  defaultViewTransition: {
    types: ({ pathChanged }) => (reducedMotion() ? false : [pathChanged ? 'page' : 'state']),
  },
})

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router
  }
}

setupTelegram()
installMotion()
applyTheme(readThemePreference())

// Inside Telegram the user is already known: trade initData for a session before the first
// route decides whether to show the login screen. Telegram keeps its splash up meanwhile.
if (inTelegram) await signInWithMiniApp().catch(() => false)
const invitation = getMiniAppInvitation()
if (invitation && window.location.pathname === '/')
  await router.navigate({ to: '/i/$code', params: { code: invitation }, search: { via: 'bot' } })

const rootElement = document.getElementById('root')
if (!rootElement) throw new Error('#root is missing from index.html')

createRoot(rootElement).render(
  <StrictMode>
    <QueryClientProvider client={queryClient}>
      <RouterProvider router={router} />
    </QueryClientProvider>
  </StrictMode>,
)
